import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  linkText?: string;
  linkTo?: string;
  duration?: number;
  onClose: () => void;
}

const variantConfig: Record<
  ToastVariant,
  {
    bg: string;
    border: string;
    text: string;
    iconColor: string;
    hoverBg: string;
    linkColor: string;
    Icon: typeof CheckCircle;
  }
> = {
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-800',
    iconColor: 'text-emerald-600',
    hoverBg: 'hover:bg-emerald-100',
    linkColor: 'text-emerald-600 hover:text-emerald-700',
    Icon: CheckCircle,
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    iconColor: 'text-red-600',
    hoverBg: 'hover:bg-red-100',
    linkColor: 'text-red-600 hover:text-red-700',
    Icon: XCircle,
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    iconColor: 'text-blue-600',
    hoverBg: 'hover:bg-blue-100',
    linkColor: 'text-blue-600 hover:text-blue-700',
    Icon: Info,
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    iconColor: 'text-amber-600',
    hoverBg: 'hover:bg-amber-100',
    linkColor: 'text-amber-600 hover:text-amber-700',
    Icon: AlertTriangle,
  },
};

export function Toast({
  message,
  variant = 'success',
  linkText,
  linkTo,
  duration = 6000,
  onClose,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  const config = variantConfig[variant];
  const { Icon } = config;

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, duration - 500);

    const closeTimer = setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed top-4 left-1/2 -translate-x-1/2 z-[100]
        flex items-center gap-3 px-4 py-3
        ${config.bg} border ${config.border} rounded-xl shadow-lg
        transition-opacity duration-500
        ${isFading ? 'opacity-0' : 'opacity-100'}
      `}
    >
      <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} />
      <span className={`${config.text} font-medium`}>{message}</span>
      {linkText && linkTo && (
        <Link
          to={linkTo}
          className={`${config.linkColor} font-semibold underline underline-offset-2`}
        >
          {linkText}
        </Link>
      )}
      <button
        onClick={() => {
          setIsFading(true);
          setTimeout(onClose, 300);
        }}
        className={`p-1 ${config.hoverBg} rounded-lg transition-colors ml-2`}
      >
        <X className={`w-4 h-4 ${config.iconColor}`} />
      </button>
    </div>
  );
}
